import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Users2,
  Stethoscope,
  ShieldAlert,
  GraduationCap,
  Trash2,
  Pencil,
  Save,
  X,
  Plus,
  Workflow,
  Sparkles,
  Loader2,
  ArrowDown,
} from "lucide-react";
import { riskLevel, riskLevelLabel, riskLevelClasses } from "@/lib/risk";

const TIPO_BADGE = {
  infortunio: "bg-rose-100 text-rose-700 border-rose-200",
  salute: "bg-amber-100 text-amber-700 border-amber-200",
  trasversale: "bg-sky-100 text-sky-700 border-sky-200",
};

function RiskBadge({ level }) {
  return (
    <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${riskLevelClasses(level)}`}>
      {riskLevelLabel(level)}
    </span>
  );
}

/**
 * MansioneCard — visualizza/edita una mansione analizzata.
 * L'utente può modificare TUTTI i campi generati dall'AI: rischi (nome, tipo, P/D con
 * ricalcolo R, descrizione, normativa), DPI, formazione, sorveglianza sanitaria,
 * attrezzature/sostanze tipiche, misure preventive, procedura operativa.
 */
export default function MansioneCard({ item, index, onSave, onRemove }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item);
  const [flowLoading, setFlowLoading] = useState(false);
  const [flowDesc, setFlowDesc] = useState("");

  const startEdit = () => {
    setDraft(JSON.parse(JSON.stringify(item)));
    setEditing(true);
  };
  const cancel = () => {
    setDraft(item);
    setEditing(false);
  };
  const save = () => {
    onSave({ ...draft, _edited: true });
    setEditing(false);
  };
  const setField = (k, v) => setDraft({ ...draft, [k]: v });

  // Rischi helpers (con ricalcolo R e livello)
  const updateRisk = (i, field, val) => {
    const arr = [...(draft.rischi || [])];
    arr[i] = { ...arr[i], [field]: val };
    if (field === "P" || field === "D") {
      const P = Number(arr[i].P) || 0;
      const D = Number(arr[i].D) || 0;
      arr[i].R = String(P * D);
      arr[i].livello = riskLevel(P, D);
    }
    setDraft({ ...draft, rischi: arr });
  };
  const addRisk = () =>
    setDraft({
      ...draft,
      rischi: [
        ...(draft.rischi || []),
        { nome: "", tipo: "infortunio", P: 1, D: 1, R: "1", livello: "molto_basso", descrizione: "", normativa: "" },
      ],
    });
  const removeRisk = (i) =>
    setDraft({ ...draft, rischi: (draft.rischi || []).filter((_, idx) => idx !== i) });

  // Generic {tipo, norma_en, note} — DPI
  const updateDpi = (i, field, val) => {
    const arr = [...(draft.dpi_proposti || [])];
    arr[i] = { ...arr[i], [field]: val };
    setDraft({ ...draft, dpi_proposti: arr });
  };
  const addDpi = () =>
    setDraft({ ...draft, dpi_proposti: [...(draft.dpi_proposti || []), { tipo: "", norma_en: "" }] });
  const removeDpi = (i) =>
    setDraft({ ...draft, dpi_proposti: (draft.dpi_proposti || []).filter((_, idx) => idx !== i) });

  // Formazione {corso, ore, periodicita, normativa}
  const updateForm = (i, field, val) => {
    const arr = [...(draft.formazione || [])];
    arr[i] = { ...arr[i], [field]: val };
    setDraft({ ...draft, formazione: arr });
  };
  const addForm = () =>
    setDraft({
      ...draft,
      formazione: [...(draft.formazione || []), { corso: "", ore: 8, periodicita: "quinquennale", normativa: "" }],
    });
  const removeForm = (i) =>
    setDraft({ ...draft, formazione: (draft.formazione || []).filter((_, idx) => idx !== i) });

  const updateStr = (key, i, val) => {
    const arr = [...(draft[key] || [])];
    arr[i] = val;
    setDraft({ ...draft, [key]: arr });
  };
  const addStr = (key) => setDraft({ ...draft, [key]: [...(draft[key] || []), ""] });
  const removeStr = (key, i) =>
    setDraft({ ...draft, [key]: (draft[key] || []).filter((_, idx) => idx !== i) });

  const view = editing ? draft : item;

  return (
    <div className="border border-border rounded-md p-4 bg-card" data-testid={`s4-card-${index}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          {editing ? (
            <>
              <Label className="text-[10px]">Mansione</Label>
              <Input value={draft.mansione || ""} onChange={(e) => setField("mansione", e.target.value)} />
            </>
          ) : (
            <h3 className="font-semibold flex items-center gap-2">
              <Users2 className="w-4 h-4 text-muted-foreground" />
              {view.mansione}
              {item._edited && (
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200"
                  title="Modificato manualmente"
                >
                  Modificato
                </span>
              )}
            </h3>
          )}
          {view.sorveglianza_sanitaria?.obbligo && !editing && (
            <span className="inline-flex items-center gap-1 text-[11px] mt-1 px-2 py-0.5 rounded bg-rose-100 text-rose-700">
              <Stethoscope className="w-3 h-3" /> Sorveglianza sanitaria obbligatoria
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {editing ? (
            <>
              <Button size="sm" className="h-8 rounded-full" onClick={save} data-testid={`s4-save-${index}`}>
                <Save className="w-3.5 h-3.5 mr-1" /> Salva
              </Button>
              <Button size="sm" variant="ghost" className="h-8" onClick={cancel} data-testid={`s4-cancel-${index}`}>
                <X className="w-3.5 h-3.5" />
              </Button>
            </>
          ) : (
            <>
              <Button
                size="sm"
                variant="outline"
                className="h-8 rounded-full"
                onClick={startEdit}
                data-testid={`s4-edit-${index}`}
              >
                <Pencil className="w-3.5 h-3.5 mr-1" /> Modifica
              </Button>
              <button
                onClick={onRemove}
                className="text-muted-foreground hover:text-destructive p-1"
                data-testid={`s4-remove-${index}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Descrizione input (contesto fornito) */}
      {view._descrizione_input && !editing && (
        <div className="mt-3 text-xs text-muted-foreground italic border-l-2 border-slate-300 pl-2">
          Descrizione fornita: {view._descrizione_input}
        </div>
      )}
      {editing && (
        <div className="mt-3">
          <Label className="text-[10px]">Descrizione mansione (contesto)</Label>
          <Textarea
            rows={2}
            value={draft._descrizione_input || ""}
            onChange={(e) => setField("_descrizione_input", e.target.value)}
          />
        </div>
      )}

      {/* Rischi */}
      <div className="mt-3">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
          <ShieldAlert className="w-3 h-3" /> Rischi ({(view.rischi || []).length})
          {editing && (
            <Button size="sm" variant="ghost" className="h-6 ml-1 text-xs" onClick={addRisk}>
              <Plus className="w-3 h-3 mr-0.5" /> aggiungi
            </Button>
          )}
        </div>
        {editing ? (
          <div className="space-y-2 mt-1">
            {(draft.rischi || []).map((r, i) => (
              <div key={`r-${i}`} className="border border-border rounded p-2 bg-muted/20">
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-5">
                    <Label className="text-[10px]">Nome</Label>
                    <Input value={r.nome || ""} onChange={(e) => updateRisk(i, "nome", e.target.value)} />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-[10px]">Tipo</Label>
                    <select
                      value={r.tipo || "infortunio"}
                      onChange={(e) => updateRisk(i, "tipo", e.target.value)}
                      className="w-full h-9 rounded border border-input bg-background px-2 text-sm"
                    >
                      <option value="infortunio">infortunio</option>
                      <option value="salute">salute</option>
                      <option value="trasversale">trasversale</option>
                    </select>
                  </div>
                  <div className="col-span-1">
                    <Label className="text-[10px]">P</Label>
                    <Input type="number" min={1} max={4} value={r.P || 1} onChange={(e) => updateRisk(i, "P", e.target.value)} />
                  </div>
                  <div className="col-span-1">
                    <Label className="text-[10px]">D</Label>
                    <Input type="number" min={1} max={4} value={r.D || 1} onChange={(e) => updateRisk(i, "D", e.target.value)} />
                  </div>
                  <div className="col-span-1">
                    <Label className="text-[10px]">R</Label>
                    <Input value={r.R || ""} readOnly className="bg-muted/40" />
                  </div>
                  <div className="col-span-1">
                    <Button size="sm" variant="ghost" className="h-9 w-9 p-0 mt-4" onClick={() => removeRisk(i)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  <div className="col-span-12">
                    <Label className="text-[10px]">Descrizione</Label>
                    <Textarea rows={2} value={r.descrizione || ""} onChange={(e) => updateRisk(i, "descrizione", e.target.value)} />
                  </div>
                  <div className="col-span-12">
                    <Label className="text-[10px]">Normativa</Label>
                    <Input value={r.normativa || ""} onChange={(e) => updateRisk(i, "normativa", e.target.value)} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {(view.rischi || []).map((r, i) => (
              <span
                key={`risk-${r.nome || i}-${i}`}
                title={`${r.descrizione || ""}${r.normativa ? " · " + r.normativa : ""}`}
                className={`text-xs px-2 py-0.5 rounded border flex items-center gap-1 ${TIPO_BADGE[r.tipo] || "bg-slate-100 text-slate-700 border-slate-200"}`}
              >
                {r.nome}
                {(r.P || r.D) && <RiskBadge level={r.livello || riskLevel(r.P, r.D)} />}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* DPI */}
      <div className="mt-3 text-sm">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
          DPI ({(view.dpi_proposti || []).length})
          {editing && (
            <Button size="sm" variant="ghost" className="h-6 ml-1 text-xs" onClick={addDpi}>
              <Plus className="w-3 h-3 mr-0.5" /> aggiungi
            </Button>
          )}
        </div>
        {editing ? (
          <div className="space-y-1 mt-1">
            {(draft.dpi_proposti || []).map((d, i) => (
              <div key={`dpi-${i}`} className="grid grid-cols-12 gap-2 items-center">
                <Input className="col-span-6" placeholder="Tipo" value={d.tipo || ""} onChange={(e) => updateDpi(i, "tipo", e.target.value)} />
                <Input className="col-span-5" placeholder="Norma EN" value={d.norma_en || ""} onChange={(e) => updateDpi(i, "norma_en", e.target.value)} />
                <Button size="sm" variant="ghost" onClick={() => removeDpi(i)} className="h-9 w-9 p-0 col-span-1">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <ul className="mt-1 space-y-0.5">
            {(view.dpi_proposti || []).map((d, i) => (
              <li key={`dpi-${d.tipo || i}-${i}`}>
                • {d.tipo}{" "}
                {d.norma_en && <span className="text-[11px] text-muted-foreground">({d.norma_en})</span>}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Formazione */}
      <div className="mt-3 text-sm">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
          <GraduationCap className="w-3 h-3" /> Formazione ({(view.formazione || []).length})
          {editing && (
            <Button size="sm" variant="ghost" className="h-6 ml-1 text-xs" onClick={addForm}>
              <Plus className="w-3 h-3 mr-0.5" /> aggiungi
            </Button>
          )}
        </div>
        {editing ? (
          <div className="space-y-1 mt-1">
            {(draft.formazione || []).map((f, i) => (
              <div key={`f-${i}`} className="grid grid-cols-12 gap-2 items-center">
                <Input className="col-span-5" placeholder="Corso" value={f.corso || ""} onChange={(e) => updateForm(i, "corso", e.target.value)} />
                <Input className="col-span-2" type="number" placeholder="Ore" value={f.ore || 0} onChange={(e) => updateForm(i, "ore", Number(e.target.value))} />
                <Input className="col-span-2" placeholder="Periodicità" value={f.periodicita || ""} onChange={(e) => updateForm(i, "periodicita", e.target.value)} />
                <Input className="col-span-2" placeholder="Norma" value={f.normativa || ""} onChange={(e) => updateForm(i, "normativa", e.target.value)} />
                <Button size="sm" variant="ghost" onClick={() => removeForm(i)} className="h-9 w-9 p-0 col-span-1">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <ul className="mt-1 space-y-0.5">
            {(view.formazione || []).map((f, i) => (
              <li key={`form-${f.corso || i}-${i}`}>
                • {f.corso} — {f.ore}h ({f.periodicita})
                {f.normativa && <span className="text-[11px] text-muted-foreground"> · {f.normativa}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Sorveglianza sanitaria */}
      {editing ? (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-4 gap-2 border border-border rounded p-2">
          <label className="col-span-1 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!draft.sorveglianza_sanitaria?.obbligo}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  sorveglianza_sanitaria: { ...(draft.sorveglianza_sanitaria || {}), obbligo: e.target.checked },
                })
              }
            />
            Obbligo
          </label>
          <Input
            className="col-span-1"
            placeholder="Periodicità"
            value={draft.sorveglianza_sanitaria?.periodicita || ""}
            onChange={(e) =>
              setDraft({
                ...draft,
                sorveglianza_sanitaria: { ...(draft.sorveglianza_sanitaria || {}), periodicita: e.target.value },
              })
            }
          />
          <Input
            className="col-span-1"
            placeholder="Norma"
            value={draft.sorveglianza_sanitaria?.normativa || ""}
            onChange={(e) =>
              setDraft({
                ...draft,
                sorveglianza_sanitaria: { ...(draft.sorveglianza_sanitaria || {}), normativa: e.target.value },
              })
            }
          />
          <Input
            className="col-span-4"
            placeholder="Esami (separati da virgola)"
            value={(draft.sorveglianza_sanitaria?.esami || []).join(", ")}
            onChange={(e) =>
              setDraft({
                ...draft,
                sorveglianza_sanitaria: {
                  ...(draft.sorveglianza_sanitaria || {}),
                  esami: e.target.value.split(",").map((x) => x.trim()).filter(Boolean),
                },
              })
            }
          />
        </div>
      ) : (
        view.sorveglianza_sanitaria?.obbligo && (
          <div className="mt-3 text-sm">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Sorveglianza sanitaria</div>
            <div>
              Periodicità: <b>{view.sorveglianza_sanitaria.periodicita || "—"}</b> — Esami:{" "}
              {(view.sorveglianza_sanitaria.esami || []).join(", ") || "—"}
            </div>
            {view.sorveglianza_sanitaria.normativa && (
              <div className="text-[11px] text-muted-foreground">Rif: {view.sorveglianza_sanitaria.normativa}</div>
            )}
          </div>
        )
      )}

      {/* Attrezzature e sostanze tipiche */}
      {editing ? (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px]">Attrezzature tipiche (una per riga)</Label>
            <Textarea
              rows={3}
              value={(draft.attrezzature_tipiche || []).join("\n")}
              onChange={(e) =>
                setDraft({ ...draft, attrezzature_tipiche: e.target.value.split("\n").filter((x) => x.trim()) })
              }
            />
          </div>
          <div>
            <Label className="text-[10px]">Sostanze tipiche (una per riga)</Label>
            <Textarea
              rows={3}
              value={(draft.sostanze_tipiche || []).join("\n")}
              onChange={(e) =>
                setDraft({ ...draft, sostanze_tipiche: e.target.value.split("\n").filter((x) => x.trim()) })
              }
            />
          </div>
        </div>
      ) : (
        (view.attrezzature_tipiche?.length > 0 || view.sostanze_tipiche?.length > 0) && (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-muted-foreground">
            {view.attrezzature_tipiche?.length > 0 && (
              <div>Attrezzature tipiche: {view.attrezzature_tipiche.join(", ")}</div>
            )}
            {view.sostanze_tipiche?.length > 0 && <div>Sostanze tipiche: {view.sostanze_tipiche.join(", ")}</div>}
          </div>
        )
      )}

      {/* Misure preventive */}
      {editing ? (
        <div className="mt-3">
          <Label className="text-[10px]">Misure preventive (una per riga)</Label>
          <Textarea
            rows={3}
            value={(draft.misure_preventive || []).join("\n")}
            onChange={(e) =>
              setDraft({ ...draft, misure_preventive: e.target.value.split("\n").filter((x) => x.trim()) })
            }
          />
        </div>
      ) : (
        view.misure_preventive?.length > 0 && (
          <div className="mt-3 text-sm">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Misure preventive</div>
            <ul className="mt-1 space-y-0.5">
              {view.misure_preventive.map((m, i) => (
                <li key={`mp-${i}-${(m || "").slice(0, 20)}`}>• {m}</li>
              ))}
            </ul>
          </div>
        )
      )}

      {/* Procedura operativa */}
      {editing ? (
        <div className="mt-3">
          <Label className="text-[10px]">Procedura operativa</Label>
          <Textarea rows={3} value={draft.procedura_operativa || ""} onChange={(e) => setField("procedura_operativa", e.target.value)} />
        </div>
      ) : (
        view.procedura_operativa && (
          <div className="mt-3 text-sm bg-muted/30 rounded p-3">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">Procedura operativa</div>
            {view.procedura_operativa}
          </div>
        )
      )}

      {/* Diagramma di flusso attività (nuovo — iter 36) */}
      <MansioneFlowEditor
        editing={editing}
        draft={draft}
        setDraft={setDraft}
        view={view}
        mansioneName={view.mansione || draft.mansione || ""}
        flowLoading={flowLoading}
        setFlowLoading={setFlowLoading}
        flowDesc={flowDesc}
        setFlowDesc={setFlowDesc}
        index={index}
      />
    </div>
  );
}

/**
 * MansioneFlowEditor — Sezione dedicata alle attività del flusso.
 * Mostra la lista in view mode + editor in edit mode + bottone AI generate.
 */
function MansioneFlowEditor({ editing, draft, setDraft, view, mansioneName, flowLoading, setFlowLoading, flowDesc, setFlowDesc, index }) {
  const activities = editing ? (draft.attivita_flusso || []) : (view.attivita_flusso || []);

  const addStep = () =>
    setDraft({
      ...draft,
      attivita_flusso: [
        ...(draft.attivita_flusso || []),
        { ordine: (draft.attivita_flusso?.length || 0) + 1, nome: "", attore: "", descrizione: "" },
      ],
    });

  const updateStep = (i, field, val) => {
    const arr = [...(draft.attivita_flusso || [])];
    arr[i] = { ...arr[i], [field]: val };
    setDraft({ ...draft, attivita_flusso: arr });
  };

  const removeStep = (i) =>
    setDraft({ ...draft, attivita_flusso: (draft.attivita_flusso || []).filter((_, idx) => idx !== i) });

  const generateWithAI = async () => {
    const desc = flowDesc.trim() || draft._descrizione_input?.trim() || view._descrizione_input?.trim() || "";
    if (!desc) {
      toast.error("Inserisci una descrizione dettagliata delle attività per generare il flusso");
      return;
    }
    setFlowLoading(true);
    try {
      const r = await api.post("/dvr/ai/mansione-flowchart", {
        mansione: mansioneName || "Mansione",
        descrizione: desc,
      });
      const arr = r.data?.attivita_flusso || [];
      if (arr.length === 0) {
        toast.error("L'AI non ha restituito attività");
      } else {
        setDraft({ ...draft, attivita_flusso: arr });
        toast.success(`${arr.length} attività generate dall'AI`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI error");
    } finally {
      setFlowLoading(false);
    }
  };

  if (!editing && activities.length === 0) return null;

  return (
    <div className="mt-4 border-t border-border pt-3" data-testid={`s4-flowchart-${index}`}>
      <div className="text-[11px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
        <Workflow className="w-3 h-3" /> Diagramma di flusso attività ({activities.length})
        {editing && (
          <>
            <Button size="sm" variant="ghost" className="h-6 ml-2 text-xs" onClick={addStep} data-testid={`s4-flow-add-${index}`}>
              <Plus className="w-3 h-3 mr-0.5" /> aggiungi passo
            </Button>
          </>
        )}
      </div>
      {editing && (
        <div className="mt-2 border border-dashed border-border rounded p-2 bg-muted/10 space-y-2">
          <Label className="text-[10px]">Descrizione dettagliata attività (per generazione AI)</Label>
          <Textarea
            rows={3}
            placeholder="Es. l'addetto preleva il materiale dal magazzino, lo trasporta al banco di lavoro, esegue il taglio con la sega circolare, e il preposto verifica la qualità del taglio prima dell'imballaggio."
            value={flowDesc || draft._descrizione_input || ""}
            onChange={(e) => setFlowDesc(e.target.value)}
            data-testid={`s4-flow-desc-${index}`}
          />
          <Button
            size="sm"
            onClick={generateWithAI}
            disabled={flowLoading}
            className="rounded-full"
            data-testid={`s4-flow-gen-${index}`}
          >
            {flowLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
            Genera flusso con AI
          </Button>
        </div>
      )}
      {editing ? (
        <div className="mt-2 space-y-2">
          {(draft.attivita_flusso || []).map((s, i) => (
            <div key={`fl-${i}`} className="grid grid-cols-12 gap-2 border border-border rounded p-2 bg-muted/10">
              <div className="col-span-1">
                <Label className="text-[10px]">#</Label>
                <Input
                  type="number"
                  min={1}
                  value={s.ordine ?? i + 1}
                  onChange={(e) => updateStep(i, "ordine", Number(e.target.value))}
                  data-testid={`s4-flow-ord-${index}-${i}`}
                />
              </div>
              <div className="col-span-4">
                <Label className="text-[10px]">Nome passo</Label>
                <Input value={s.nome || ""} onChange={(e) => updateStep(i, "nome", e.target.value)} data-testid={`s4-flow-nome-${index}-${i}`} />
              </div>
              <div className="col-span-3">
                <Label className="text-[10px]">Attore</Label>
                <Input placeholder="es. Operatore, Preposto…" value={s.attore || ""} onChange={(e) => updateStep(i, "attore", e.target.value)} data-testid={`s4-flow-att-${index}-${i}`} />
              </div>
              <div className="col-span-3">
                <Label className="text-[10px]">Descrizione</Label>
                <Input value={s.descrizione || ""} onChange={(e) => updateStep(i, "descrizione", e.target.value)} data-testid={`s4-flow-desc-item-${index}-${i}`} />
              </div>
              <div className="col-span-1 flex items-end">
                <Button size="sm" variant="ghost" onClick={() => removeStep(i)} className="h-9 w-9 p-0" data-testid={`s4-flow-remove-${index}-${i}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <FlowchartPreview steps={activities} />
      )}
    </div>
  );
}

/**
 * FlowchartPreview — rendering visuale delle attività (verticale o swim-lane preview).
 */
function FlowchartPreview({ steps }) {
  if (!steps || steps.length === 0) return null;
  const sorted = [...steps].sort((a, b) => (a.ordine || 0) - (b.ordine || 0));
  const attori = Array.from(new Set(sorted.map((s) => (s.attore || "").trim()).filter(Boolean)));
  const isSwimLane = attori.length > 1;

  const COLORS = ["bg-sky-100 border-sky-300 text-sky-900", "bg-emerald-100 border-emerald-300 text-emerald-900", "bg-amber-100 border-amber-300 text-amber-900", "bg-violet-100 border-violet-300 text-violet-900", "bg-rose-100 border-rose-300 text-rose-900"];
  const attoreColor = (a) => {
    const idx = attori.findIndex((x) => x.toLowerCase() === (a || "").toLowerCase());
    return COLORS[idx >= 0 ? idx % COLORS.length : 0];
  };

  if (!isSwimLane) {
    return (
      <div className="mt-2 flex flex-col items-center gap-1">
        {sorted.map((s, i) => (
          <div key={`fp-${i}`} className="w-full max-w-md flex flex-col items-center">
            <div className={`w-full text-center rounded-md border-2 px-3 py-2 ${attoreColor(s.attore)}`}>
              <div className="text-xs font-semibold">{s.ordine || i + 1}. {s.nome}</div>
              {s.descrizione && <div className="text-[10px] italic mt-0.5 opacity-80">{s.descrizione}</div>}
            </div>
            {i < sorted.length - 1 && <ArrowDown className="w-4 h-4 my-0.5 text-muted-foreground" />}
          </div>
        ))}
      </div>
    );
  }

  // Swim lane preview
  return (
    <div className="mt-2 overflow-x-auto">
      <div className="grid gap-0" style={{ gridTemplateColumns: `repeat(${attori.length}, minmax(140px, 1fr))` }}>
        {attori.map((a, i) => (
          <div key={`hd-${a}`} className={`text-center text-xs font-bold py-1.5 border ${attoreColor(a)}`}>
            {a}
          </div>
        ))}
        {sorted.map((s, i) => (
          attori.map((a, ci) => {
            const isMine = (s.attore || "").toLowerCase() === a.toLowerCase();
            return (
              <div key={`sl-${i}-${ci}`} className={`min-h-[54px] p-1.5 border ${isMine ? attoreColor(a) : "bg-slate-50 border-slate-200"}`}>
                {isMine && (
                  <div>
                    <div className="text-[11px] font-semibold text-center">{s.ordine || i + 1}. {s.nome}</div>
                    {s.descrizione && <div className="text-[10px] italic text-center mt-0.5 opacity-80">{s.descrizione}</div>}
                  </div>
                )}
              </div>
            );
          })
        ))}
      </div>
    </div>
  );
}
